import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons/faShieldCheck';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { faMessageQuestion } from '@fortawesome/pro-solid-svg-icons/faMessageQuestion';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faCamera } from '@fortawesome/pro-solid-svg-icons/faCamera';
import { faSun } from '@fortawesome/pro-solid-svg-icons/faSun';
import { faMoon } from '@fortawesome/pro-solid-svg-icons/faMoon';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons/faCalendar';

import { PhotoGuideModal } from 'us-react-components/dist/PhotoGuideModal/PhotoGuideModal';
import { ToggleSwitch } from 'us-react-components/dist/ToggleSwitch/ToggleSwitch';
import {
  getLongWeekdayDateString,
  getShortDateString,
  getTimeOfDayString,
  sameDay,
} from 'us-react-components/dist/utils/date';
import {
  axios,
  Include,
  IncludeService,
  Job,
  JobService,
  ParentRateSuggestionService,
  Sweetener,
  SweetenerService,
  UserPictureService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

type VerticalCode = 'childCare' | 'seniorCare' | 'petCare' | 'householdServices';
type JobType = 'one_time' | 'part_time' | 'full_time';

const VERTICAL_TYPE_ID: Record<VerticalCode, number> = {
  childCare: 1,
  petCare: 2,
  seniorCare: 3,
  householdServices: 4,
};

const VERTICAL_CARDS: Array<{ code: VerticalCode; title: string; body: string }> = [
  {
    code: 'childCare',
    title: 'Child Care',
    body: 'Backup, occasional, part- or full-time sitters and nannies',
  },
  { code: 'seniorCare', title: 'Senior Care', body: 'Non-medical companion care' },
  { code: 'petCare', title: 'Pet Care', body: 'Dogs, cats, and other animals' },
  {
    code: 'householdServices',
    title: 'Household Services',
    body: 'Cleaning, errands, house sitting, or other services',
  },
];

const JOB_TYPE_CARDS: Array<{ code: JobType; title: string; body: string }> = [
  { code: 'one_time', title: 'Specific Date', body: 'Last-minute backup care, occasional care, etc.' },
  { code: 'part_time', title: 'Part-time', body: '25 hours or less per week' },
  { code: 'full_time', title: 'Full-time', body: 'More than 25 hours per week' },
];

const MAX_DESCRIPTION = 2500;
const MAX_QUESTIONS = 3;

interface JobBookingExtras {
  typeId?: number;
  numberOfChildren?: number;
  kidsAges?: string;
  address?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    neighborhood?: string;
  };
  rate?: number;
  paymentType?: string;
  description?: string;
  start?: string;
  end?: string;
  includes?: Include[];
  sweeteners?: Sweetener[];
}

type JobTypeServer = 'one_time' | 'recurring';

interface JobExtras extends Omit<Job, 'booking' | 'type' | 'recurring' | 'availability'> {
  type?: JobTypeServer;
  availability?: string;
  booking?: JobBookingExtras;
  minimumHours?: number;
  questions?: Array<{ id?: number; text?: string }>;
  recurring?: {
    flexible?: boolean;
    startDate?: string;
    endDate?: string;
    timeOfDay?: { code?: string; label?: string };
    days?: number[]; // 0 Sun .. 6 Sat
    daySchedule?: Record<number, { startTime?: string; endTime?: string }>;
  };
}

const PAYMENT_TYPES: Array<{ code: string; label: string }> = [
  { code: '', label: 'payment method' },
  { code: 'cash', label: 'cash' },
  { code: 'credit', label: 'credit card' },
];

const TIMES_OF_DAY: Array<{ code: string; label: string }> = [
  { code: 'day', label: 'During the day' },
  { code: 'night', label: 'Evening or night time' },
  { code: 'specific', label: 'Specific hours' },
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_LONG_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

// Angular source: /v2/suggested-questions. Fallback set keeps the UI useful
// when the endpoint returns empty or 404s in lower envs.
const DEFAULT_SAMPLE_QUESTIONS: Array<{ question: string }> = [
  { question: "What's your favorite activity to do with kids?" },
  { question: 'How would you handle an emergency?' },
  { question: 'Do you have CPR / first-aid certification?' },
  { question: 'What age groups do you have the most experience with?' },
  { question: 'Are you comfortable with light housekeeping and meal prep?' },
  { question: 'Do you have reliable transportation?' },
  { question: 'Can you provide references from past families?' },
];

// Server expects dates as local-time "YYYY-MM-DD HH:mm:ss" (Jackson parser, not
// ISO 8601). We keep state in matching local-time format (with a T separator so
// `new Date(...)` parses it deterministically across browsers) and convert to
// the server format just before submit.
const pad = (n: number): string => String(n).padStart(2, '0');
const toLocalDateTime = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
  `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
const toApiDateTime = (s?: string): string | undefined => {
  if (!s) return undefined;
  const withSeconds = s.length === 16 ? `${s}:00` : s; // "YYYY-MM-DDTHH:mm" → add ":00"
  const normalized = withSeconds.length === 10 ? `${withSeconds}T00:00:00` : withSeconds;
  return normalized.replace('T', ' ');
};

// 30-min slots in 12-hour "h:mm AM/PM" labels; underlying value stays 24-hour
// "HH:mm" so it can be spliced back into the "YYYY-MM-DDTHH:mm:ss" state.
interface TimeSlot { value: string; label: string }
const TIME_SLOTS: TimeSlot[] = ((): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  for (let h = 0; h < 24; h += 1) {
    for (const m of [0, 30]) {
      const value = `${pad(h)}:${pad(m)}`;
      const period = h < 12 ? 'AM' : 'PM';
      const h12 = h % 12 === 0 ? 12 : h % 12;
      slots.push({ value, label: `${h12}:${pad(m)} ${period}` });
    }
  }
  return slots;
})();

// Split "YYYY-MM-DDTHH:mm:ss" state value into separate date ("YYYY-MM-DD") and
// time ("HH:mm") halves for the two-control picker; each half falls back to ''.
const splitDateTime = (s?: string): { date: string; time: string } => {
  if (!s) return { date: '', time: '' };
  const [date, rest] = s.split('T');
  const time = rest ? rest.slice(0, 5) : '';
  return { date: date ?? '', time };
};

const joinDateTime = (date: string, time: string): string | undefined => {
  if (!date || !time) return undefined;
  return `${date}T${time}:00`;
};

const parseLocal = (s?: string): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const emptyJob = (): JobExtras => ({
  type: undefined,
  booking: {
    numberOfChildren: 1,
    address: {},
    paymentType: '',
    includes: [],
    sweeteners: [],
  },
  questions: [{ text: '' }],
});

const ProfilePhotoUploader = ({
  parentId,
  pictureUrl,
  onUploaded,
}: {
  parentId?: number;
  pictureUrl?: string;
  onUploaded: () => void;
}): JSX.Element => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  const onChange = async (file: File): Promise<void> => {
    if (!parentId) return;
    if (file.size > 10 * 1024 * 1024) {
      setErr('Photo too large (10 MB max).');
      return;
    }
    setUploading(true);
    setErr('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', String(parentId));
      await UserPictureService.create(formData as unknown as { userId?: number; file?: string });
      setLocalPreview(URL.createObjectURL(file));
      onUploaded();
    } catch (e) {
      const serverMsg =
        (e as { response?: { data?: { errors?: Array<{ message?: string }> } } })
          ?.response?.data?.errors?.[0]?.message;
      setErr(serverMsg ?? "Couldn't upload. Try a different photo.");
    } finally {
      setUploading(false);
    }
  };

  const hasStoredPhoto = !!pictureUrl && pictureUrl.indexOf('us-default-avatar') === -1;
  const displayPhoto = localPreview ?? (hasStoredPhoto ? pictureUrl : null);
  const hasPhoto = !!displayPhoto;

  return (
    <div className='flex flex-col items-center mb-4'>
      <label className='cursor-pointer relative inline-block'>
        <input
          type='file'
          accept='image/*'
          className='sr-only'
          onChange={(e): void => {
            const f = e.target.files?.[0];
            if (f) void onChange(f);
          }}
        />
        {hasPhoto ? (
          <img
            alt='Profile'
            src={displayPhoto as string}
            className='w-24 h-24 rounded-full object-cover border-2 border-neutral-300'
          />
        ) : (
          <div className='w-24 h-24 rounded-full border-2 border-dashed border-sapphire flex items-center justify-center text-center text-xs p-2 text-sapphire'>
            Upload profile photo
          </div>
        )}
        <span
          aria-hidden='true'
          className={`absolute bottom-0 right-0 w-9 h-9 inline-flex items-center justify-center bg-white rounded-full border border-neutral-300 shadow ${
            hasPhoto ? 'text-sapphire' : 'text-neutral-400'
          }`}
        >
          <FontAwesomeIcon icon={faCamera} />
        </span>
      </label>
      {uploading && <p className='text-sm mt-1'>Uploading…</p>}
      {err && <p className='text-red-600 text-sm mt-1'>{err}</p>}
      {!hasPhoto && !uploading && !err && (
        <p className='text-red-600 text-sm mt-1'>
          Profile photo required to post a job.
        </p>
      )}
      <Button
        buttonType={ButtonType.tertiary}
        className='mt-1 text-sm underline'
        onClick={(): void => setGuideOpen(true)}
      >
        Photo tips
      </Button>
      <PhotoGuideModal
        isOpen={guideOpen}
        onClose={(): void => setGuideOpen(false)}
        variant='parent'
      />
    </div>
  );
};

export const JobPost = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: editingJobId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;
  const parent = stores.parent.parent;

  // Mirrors Angular post.js:63,715-723.
  // isFromNux: user arrived via family-reg → post-a-job hand-off.
  // presetType: a non-NUX caller pre-selected the job type via ?type= or ?bookingType=.
  const isFromNux = searchParams.get('fromNux') === 'true';
  const typeParam = searchParams.get('type');
  const bookingTypeParam = searchParams.get('bookingType');
  const presetType =
    !isFromNux && (typeParam === 'recurring' || typeParam === 'one_time' || !!bookingTypeParam);

  const [job, setJob] = useState<JobExtras>(emptyJob);
  const [vertical, setVertical] = useState<VerticalCode | null>(null);
  const [step, setStep] = useState<number>(1);
  const [maxStep, setMaxStep] = useState<number>(6);
  const [includes, setIncludes] = useState<Array<Include & { selected?: boolean }>>([]);
  const [sweeteners, setSweeteners] = useState<Array<Sweetener & { selected?: boolean }>>([]);
  const [negotiableRate, setNegotiableRate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [errorFields, setErrorFields] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(!editingJobId);
  const [sampleQuestions, setSampleQuestions] = useState<Array<{ question: string }>>([]);
  const [shownSampleIndex, setShownSampleIndex] = useState(0);
  const [suggestedRate, setSuggestedRate] = useState<{ rate?: number; label?: string } | null>(
    null,
  );
  const [photoJustUploaded, setPhotoJustUploaded] = useState(false);
  const [isOvernight, setIsOvernight] = useState(false);

  useEffect(() => {
    document.title = editingJobId ? 'UrbanSitter - Edit Job' : 'UrbanSitter - Post a Job';
  }, [editingJobId]);

  // Prefill from an existing job if we're in edit mode, or from search params
  // (the posts page lets you duplicate a closed job via ?presetId=).
  useEffect(() => {
    const toLoadId = editingJobId ?? searchParams.get('presetId');
    if (!toLoadId || !user?.id) {
      setLoaded(true);
      return;
    }
    const load = async (): Promise<void> => {
      try {
        const resp = await JobService.findById(Number(toLoadId), { params: { userId: user.id } });
        const loadedJob = (resp.data?.data ?? null) as JobExtras | null;
        if (!loadedJob) return;
        if (!loadedJob.questions || loadedJob.questions.length === 0) {
          loadedJob.questions = [{ text: '' }];
        }
        // API returns recurring.days as RecurringDay[] with weekday.id 1..7;
        // local state stores day indexes 0..6 (Sun..Sat) so the picker's
        // includes()/toggle logic matches the server-sent shape on save.
        if (loadedJob.recurring?.days) {
          const rawDays = loadedJob.recurring.days as unknown as Array<
            { weekday?: { id?: number }; startTime?: string; endTime?: string } | number
          >;
          const daySchedule: Record<number, { startTime?: string; endTime?: string }> = {};
          loadedJob.recurring.days = rawDays
            .map((rawDay) => {
              if (typeof rawDay === 'number') return rawDay;
              const dayIndex = (rawDay?.weekday?.id ?? 0) - 1;
              if (dayIndex >= 0 && (rawDay?.startTime || rawDay?.endTime)) {
                daySchedule[dayIndex] = { startTime: rawDay.startTime, endTime: rawDay.endTime };
              }
              return dayIndex;
            })
            .filter((dayIndex) => dayIndex >= 0 && dayIndex <= 6);
          loadedJob.recurring.daySchedule = daySchedule;
        }
        // The API stores recurring dates on booking.start/end and the time of
        // day as a summary string; rebuild the form fields the save path
        // derives them from, otherwise edit mode hides the schedule section.
        if (loadedJob.type === 'recurring' && loadedJob.recurring) {
          const timeSummary =
            (loadedJob.recurring as { timeSummary?: string }).timeSummary ?? '';
          loadedJob.recurring.flexible = timeSummary.includes('flexible days');
          const hasDayTimes = Object.keys(loadedJob.recurring.daySchedule ?? {}).length > 0;
          const timeOfDayCode = timeSummary.includes('During the day')
            ? 'day'
            : timeSummary.includes('Evening or night') ? 'night' : hasDayTimes ? 'specific' : '';
          loadedJob.recurring.timeOfDay = TIMES_OF_DAY.find((tod) => tod.code === timeOfDayCode);
          loadedJob.recurring.startDate = loadedJob.booking?.start?.slice(0, 10);
          loadedJob.recurring.endDate = loadedJob.booking?.end?.slice(0, 10);
        }
        setJob(loadedJob);
        const typeId = loadedJob.booking?.typeId;
        const code = (Object.entries(VERTICAL_TYPE_ID).find(([, v]) => v === typeId)?.[0] ?? null) as
          | VerticalCode
          | null;
        setVertical(code);
        setNegotiableRate(!loadedJob.booking?.rate);
        const loadedStart = parseLocal(loadedJob.booking?.start);
        const loadedEnd = parseLocal(loadedJob.booking?.end);
        if (loadedStart && loadedEnd && !sameDay(loadedStart, loadedEnd)) {
          setIsOvernight(true);
        }
        setStep(6);
      } catch {
        /* ignore */
      } finally {
        setLoaded(true);
      }
    };
    void load();
  }, [editingJobId, searchParams, user?.id]);

  useEffect(() => {
    const loadIncludes = async (): Promise<void> => {
      try {
        const resp = await IncludeService.getBookingIncludes();
        const list = (resp.data?.data ?? []) as Include[];
        const selectedIds = new Set((job.booking?.includes ?? []).map((i) => i.id));
        setIncludes(list.map((i) => ({ ...i, selected: selectedIds.has(i.id) })));
      } catch {
        setIncludes([]);
      }
    };
    const loadSweeteners = async (): Promise<void> => {
      try {
        const resp = await SweetenerService.getBookingSweeteners();
        const list = (resp.data?.data ?? []) as Sweetener[];
        const selectedIds = new Set((job.booking?.sweeteners ?? []).map((s) => s.id));
        setSweeteners(list.map((s) => ({ ...s, selected: selectedIds.has(s.id) })));
      } catch {
        setSweeteners([]);
      }
    };
    const loadSampleQuestions = async (): Promise<void> => {
      try {
        const resp = await axios.get<{ data?: Array<{ question: string }> }>('/v2/suggested-questions');
        const list = resp.data?.data ?? [];
        setSampleQuestions(list.length > 0 ? list : DEFAULT_SAMPLE_QUESTIONS);
      } catch {
        setSampleQuestions(DEFAULT_SAMPLE_QUESTIONS);
      }
    };
    void loadIncludes();
    void loadSweeteners();
    void loadSampleQuestions();

  }, []);

  // Suggested rate (zip-based "PRO TIP") — matches Angular JobFormService.getSuggestedRate
  useEffect(() => {
    const zip = job.booking?.address?.zip;
    if (!user?.id || !zip || zip.length !== 5) {
      setSuggestedRate(null);
      return;
    }
    const params: Record<string, string | number> = {
      userId: user.id,
      zip,
    };
    if (job.booking?.numberOfChildren !== undefined) {
      params.kids = job.booking.numberOfChildren;
    }
    if (job.booking?.start) params.start = toApiDateTime(job.booking.start) ?? '';
    const load = async (): Promise<void> => {
      try {
        const resp = await ParentRateSuggestionService.get({ params });
        setSuggestedRate((resp.data?.data ?? null) as { rate?: number; label?: string } | null);
      } catch {
        setSuggestedRate(null);
      }
    };
    void load();
  }, [
    user?.id,
    job.booking?.address?.zip,
    job.booking?.numberOfChildren,
    job.booking?.start,
  ]);

  // Default start/end times when entering the date step for one-time jobs.
  useEffect(() => {
    if (step !== 3 || job.type !== 'one_time' || job.booking?.start) return;
    const base = new Date();
    // Default to 18:00; if past 18:00 use tomorrow
    if (base.getHours() >= 18) {
      base.setDate(base.getDate() + 1);
    }
    base.setHours(18, 0, 0, 0);
    const end = new Date(base);
    end.setHours(22, 0, 0, 0);
    setBooking({ start: toLocalDateTime(base), end: toLocalDateTime(end) });
  }, [step, job.type]);

  // Prepopulate job location address from the parent profile when the address
  // is still empty.
  useEffect(() => {
    if (!parent?.address) return;
    const a = job.booking?.address;
    if (a?.street || a?.city || a?.state || a?.zip) return;
    setJob((prev) => ({
      ...prev,
      booking: {
        ...(prev.booking ?? {}),
        address: {
          ...(prev.booking?.address ?? {}),
          street: parent.address?.street ?? '',
          street2: parent.address?.street2 ?? '',
          city: parent.address?.city ?? '',
          state: parent.address?.state ?? '',
          zip: parent.address?.zip ?? '',
          neighborhood: parent.address?.neighborhood ?? '',
        },
      },
    }));
  }, [parent?.address]);

  // Rotate sample screening questions every 5s while on step 6 for childCare
  useEffect(() => {
    if (step !== 6 || job.booking?.typeId !== 1 || sampleQuestions.length === 0) return;
    const id = window.setInterval(() => {
      setShownSampleIndex((i) => (i + 1) % sampleQuestions.length);
    }, 5000);
    return (): void => window.clearInterval(id);
  }, [step, job.booking?.typeId, sampleQuestions.length]);

  const setBooking = (patch: Partial<JobBookingExtras>): void => {
    setJob((prev) => ({
      ...prev,
      booking: { ...(prev.booking ?? {}), ...patch },
    }));
  };

  const setAddress = (patch: Partial<NonNullable<JobBookingExtras['address']>>): void => {
    setJob((prev) => ({
      ...prev,
      booking: {
        ...(prev.booking ?? {}),
        address: { ...(prev.booking?.address ?? {}), ...patch },
      },
    }));
  };

  const setVerticalType = (code: VerticalCode): void => {
    setVertical(code);
    setBooking({ typeId: VERTICAL_TYPE_ID[code] });
    // householdServices skips the job-type step (always one_time)
    if (code === 'householdServices') {
      setJob((prev) => ({ ...prev, type: 'one_time' }));
      setStep(3);
    } else {
      setStep(2);
    }
  };

  const setJobType = (code: JobType): void => {
    setJob((prev) => {
      if (code === 'full_time') {
        return { ...prev, type: 'recurring', availability: 'Full Time' };
      }
      if (code === 'part_time') {
        return { ...prev, type: 'recurring', availability: 'Part Time' };
      }
      return {
        ...prev,
        type: 'one_time',
        availability: undefined,
        booking: { ...(prev.booking ?? {}), paymentType: 'cash' },
      };
    });
    setStep(3);
  };

  const getVerticalLabel = (): string =>
    VERTICAL_CARDS.find((v) => v.code === vertical)?.title ?? '';
  const getTypeLabel = (): string => {
    if (job.type === 'one_time') return ' — Specific Date';
    if (job.type === 'recurring') {
      return job.availability ? ` — ${job.availability}` : ' — Recurring';
    }
    return '';
  };

  const validateStep4 = (): { problems: string[]; fields: Set<string> } => {
    const problems: string[] = [];
    const fields = new Set<string>();
    const b = job.booking ?? {};
    if (b.typeId !== 4 && !b.numberOfChildren) {
      problems.push('Number receiving care');
      fields.add('numberOfChildren');
    }
    if (b.typeId === 1 && !b.kidsAges) {
      problems.push("Children's ages");
      fields.add('kidsAges');
    }
    if (!b.address?.city) {
      problems.push('City');
      fields.add('city');
    }
    if (!b.address?.state) {
      problems.push('State');
      fields.add('state');
    }
    if (!b.address?.zip) {
      problems.push('ZIP code');
      fields.add('zip');
    }
    if (!negotiableRate && (!b.rate || b.rate < 8)) {
      problems.push('Hourly rate (min $8)');
      fields.add('rate');
    }
    if (!b.paymentType) {
      problems.push('Payment method');
      fields.add('paymentType');
    }
    const hasDefaultAvatar = !parent?.pictureUrl || parent.pictureUrl.indexOf('us-default-avatar') > -1;
    if (hasDefaultAvatar && !photoJustUploaded) {
      problems.push('Profile photo required to post a job');
      fields.add('pictureUrl');
    }
    return { problems, fields };
  };

  const validateStep3 = (): string[] => {
    const problems: string[] = [];
    if (job.type === 'one_time') {
      if (!job.booking?.start || !job.booking?.end) {
        problems.push('Start and end date/time');
      } else {
        const s = parseLocal(job.booking.start);
        const e = parseLocal(job.booking.end);
        if (s && e && e.getTime() <= s.getTime()) {
          problems.push('Booking end time must be after the start time');
        }
      }
    } else {
      if (!job.recurring?.startDate) problems.push('Start date');
      if (!job.recurring?.timeOfDay?.code) problems.push('Time of day');
      const hasDays = job.recurring?.timeOfDay?.code === 'specific'
        ? (job.recurring?.days ?? []).length > 0
        : true;
      if (!hasDays) problems.push('Which days of the week');
    }
    return problems;
  };

  const validateStep5 = (): string[] => {
    const problems: string[] = [];
    const desc = job.booking?.description?.trim() ?? '';
    if (desc.length < 10) problems.push('Please provide details (10 characters minimum).');
    return problems;
  };

  // Mirrors Angular post.js:560-581. Preserves children/city/state/payment
  // context when bailing out of the wizard to /sitters search; recurring jobs
  // also set searchTypeCode + showSaveModal; one-time jobs pass through dates.
  const skipToSearch = (): void => {
    const b = job.booking ?? {};
    const params = new URLSearchParams();
    if (b.numberOfChildren != null) params.set('numberOfChildren', String(b.numberOfChildren));
    if (b.address?.city) params.set('city', b.address.city);
    if (b.address?.state) params.set('state', b.address.state);
    if (b.paymentType === 'credit') params.set('acceptsCreditCard', 'true');
    if (job.type === 'recurring') {
      params.set('searchTypeCode', job.availability === 'Full Time' ? 'FULL_TIME' : 'PART_TIME');
      params.set('showSaveModal', 'true');
    } else {
      if (b.start) params.set('start', String(b.start));
      if (b.end) params.set('end', String(b.end));
    }
    tracker.track('job-post-details', { action: 'skipped to search', label: 'postJob' });
    navigate(`/sitters?${params.toString()}`);
  };

  const goToStep = (next: number): void => {
    if (next > step) {
      if (step === 3) {
        const p = validateStep3();
        if (p.length > 0) {
          setErrors(p);
          setErrorFields(new Set());
          return;
        }
      } else if (step === 4) {
        const { problems, fields } = validateStep4();
        if (problems.length > 0) {
          setErrors(problems);
          setErrorFields(fields);
          return;
        }
      } else if (step === 5) {
        // Apply includes/sweeteners selections
        setBooking({
          includes: includes.filter((i) => i.selected).map((i) => ({ id: i.id })),
          sweeteners: sweeteners.filter((s) => s.selected).map((s) => ({ id: s.id })),
        });
        const p = validateStep5();
        if (p.length > 0) {
          setErrors(p);
          setErrorFields(new Set());
          return;
        }
      }
    }
    setErrors([]);
    setErrorFields(new Set());
    setStep(next);
    setMaxStep(Math.max(maxStep, next));
  };

  const saveJob = async (): Promise<void> => {
    if (!user?.id || submitting) return;
    setSubmitting(true);
    try {
      const isChildCare = job.booking?.typeId === 1;
      const payloadBooking: JobBookingExtras & { parent?: { id: number } } = {
        ...job.booking,
        parent: { id: user.id },
        includes: isChildCare
          ? includes
              .filter((i) => i.selected)
              .map((i) => ({ id: i.id, label: i.label, type: i.type }))
          : [],
        sweeteners: isChildCare
          ? sweeteners
              .filter((s) => s.selected)
              .map((s) => ({ id: s.id, label: s.label, type: s.type }))
          : [],
        rate: negotiableRate ? undefined : job.booking?.rate,
        start: toApiDateTime(job.booking?.start),
        end: toApiDateTime(job.booking?.end),
      };

      // For recurring jobs, mirror Angular: start/end on booking come from the
      // recurring date inputs, and days are sent as objects keyed by weekday code.
      if (job.type === 'recurring') {
        if (job.recurring?.startDate) payloadBooking.start = toApiDateTime(job.recurring.startDate);
        if (job.recurring?.endDate) payloadBooking.end = toApiDateTime(job.recurring.endDate);
      }

      const codesToTimeSummary: Record<string, string> = {
        day: 'During the day',
        night: 'Evening or night time',
        specific: '',
      };
      const recurringTimeCode = job.recurring?.timeOfDay?.code ?? '';
      const timeSummary =
        (codesToTimeSummary[recurringTimeCode] ?? '') +
        (job.recurring?.flexible ? ' (flexible days)' : '');
      const isSpecificHours = recurringTimeCode === 'specific';
      const recurring = job.type === 'recurring'
        ? {
            ...job.recurring,
            timeSummary,
            days: (job.recurring?.days ?? []).map((dayIndex) => {
              const schedule = job.recurring?.daySchedule?.[dayIndex] ?? {};
              return {
                weekday: { id: dayIndex + 1 },
                ...(isSpecificHours && schedule.startTime ? { startTime: schedule.startTime } : {}),
                ...(isSpecificHours && schedule.endTime ? { endTime: schedule.endTime } : {}),
              };
            }),
          }
        : undefined;

      const payload = {
        ...job,
        parentId: user.id,
        booking: payloadBooking,
        recurring,
        questions: (job.questions ?? [])
          .map((q) => ({ ...q, text: q.text?.trim() ?? '' }))
          .filter((q) => q.text.length > 0),
      };

      if (editingJobId) {
        await JobService.patch(Number(editingJobId), payload as unknown as Job);
        tracker.track('job-updated', {
          jobId: editingJobId,
          typeId: payload.booking?.typeId,
          type: payload.type,
        });
        toast.success('Job updated.');
        navigate(`/job/${editingJobId}/success`);
      } else {
        // Backend (JobStatus enum) rejects jobs without a valid status. Angular
        // sets status='new' on clone in job-form-service.js:209; new jobs need
        // it too, otherwise the API returns "please provide a valid status".
        (payload as JobExtras & { status?: string }).status = 'new';
        const resp = await JobService.create(payload as unknown as Job);
        const newId = (resp.data?.data as { id?: number } | undefined)?.id;
        tracker.track('job-posted', {
          jobId: newId,
          typeId: payload.booking?.typeId,
          type: payload.type,
        });
        toast.success('Job posted.');
        navigate(newId ? `/job/${newId}/success` : '/posts');
      }
    } catch (err) {
      const serverMsg =
        (err as { response?: { data?: { errors?: Array<{ message?: string }> } } })
          ?.response?.data?.errors?.[0]?.message;
      toast.error(serverMsg ?? "Couldn't save the job. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const addScreeningQuestion = (): void => {
    setJob((prev) => {
      const questions = [...(prev.questions ?? [])];
      if (questions.length >= MAX_QUESTIONS) return prev;
      questions.push({ text: '' });
      return { ...prev, questions };
    });
  };

  const updateQuestion = (i: number, text: string): void => {
    setJob((prev) => {
      const questions = [...(prev.questions ?? [])];
      questions[i] = { ...questions[i], text };
      return { ...prev, questions };
    });
  };

  const toggleDay = (dayIndex: number): void => {
    setJob((prev) => {
      const days = prev.recurring?.days ?? [];
      const next = days.includes(dayIndex)
        ? days.filter((d) => d !== dayIndex)
        : [...days, dayIndex];
      return { ...prev, recurring: { ...(prev.recurring ?? {}), days: next } };
    });
  };

  if (!loaded) {
    return (
      <div className='container py-8 text-center'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  const typeId = job.booking?.typeId;
  const showKidsSelect = typeId && typeId !== 4;
  const verticalLabel = getVerticalLabel();
  const typeLabel = getTypeLabel();
  const description = job.booking?.description ?? '';
  const remaining = MAX_DESCRIPTION - description.length;
  const questions = job.questions ?? [];
  const hasPhoto =
    photoJustUploaded ||
    (!!parent?.pictureUrl && parent.pictureUrl.indexOf('us-default-avatar') === -1);
  const progressSteps = [1, 2, 3, 4, 5, 6];

  return (
    <div className='container py-6 max-w-3xl mx-auto'>
      {step > 2 && vertical && (
        <p className='mb-3 text-sm uppercase font-semibold'>
          {verticalLabel}{typeLabel}{' '}
          <Button
            buttonType={ButtonType.tertiary}
            className='italic text-xs underline'
            onClick={(): void => setStep(1)}
          >
            Change
          </Button>
        </p>
      )}

      {/* Date summary: visible on step 4+ for one-time jobs (mirrors Angular date-renderer) */}
      {step > 3 && job.type === 'one_time' && job.booking?.start && (
        <div className='mb-3 text-sm pb-3 border-b border-dashed border-neutral-300'>
          <FontAwesomeIcon icon={faCalendar} className='mr-2 text-sapphire' />
          <span className='font-semibold'>
            {getLongWeekdayDateString(job.booking.start)}, {getTimeOfDayString(job.booking.start)}
            {job.booking.end ? ` – ${getTimeOfDayString(job.booking.end)}` : ''}
          </span>{' '}
          <Button
            buttonType={ButtonType.tertiary}
            className='italic text-xs underline'
            onClick={(): void => setStep(3)}
          >
            Change
          </Button>
        </div>
      )}

      {/* Date summary for recurring jobs */}
      {step > 3 && job.type === 'recurring' && job.recurring?.startDate && (
        <div className='mb-3 text-sm pb-3 border-b border-dashed border-neutral-300'>
          <FontAwesomeIcon icon={faCalendar} className='mr-2 text-sapphire' />
          <span className='font-semibold'>
            {job.availability ?? 'Recurring'}, starting{' '}
            {getShortDateString(job.recurring.startDate)}
            {job.recurring.endDate ? ` to ${getShortDateString(job.recurring.endDate)}` : ''}
          </span>
          <div className='flex flex-wrap gap-3 my-2'>
            {DAYS.map((d, i) => {
              const active = (job.recurring?.days ?? []).includes(i);
              const isNight = job.recurring?.timeOfDay?.code === 'night';
              return (
                <span key={d} className='flex flex-col items-center'>
                  <span className='text-xs text-neutral-600 font-light uppercase'>{d}</span>
                  <span
                    aria-hidden='true'
                    className={`mt-1 w-9 h-9 rounded-full flex items-center justify-center ${
                      !active
                        ? 'bg-neutral-200'
                        : isNight ? 'bg-sapphire' : 'bg-yellow-400'
                    }`}
                  >
                    {active && (
                      <FontAwesomeIcon
                        icon={isNight ? faMoon : faSun}
                        className='text-white text-lg'
                      />
                    )}
                  </span>
                </span>
              );
            })}
          </div>
          {job.recurring.timeOfDay?.label && (
            <span className='block text-neutral-700 mb-1'>
              {job.recurring.timeOfDay.label}
              {job.recurring.flexible ? ' (flexible days)' : ''}
            </span>
          )}
          <Button
            buttonType={ButtonType.tertiary}
            className='italic text-xs underline'
            onClick={(): void => setStep(3)}
          >
            Change
          </Button>
        </div>
      )}

      {/* Summary widget: visible on steps 5+ (matches Angular's ng-if="currentStep > 4") */}
      {step > 4 && (
        <div className='mb-4 flex items-start gap-3'>
          <div className='flex-shrink-0'>
            <ProfilePhotoUploader
              parentId={user?.id}
              pictureUrl={parent?.pictureUrl}
              onUploaded={(): void => {
                setPhotoJustUploaded(true);
                void stores.parent.refreshParent?.();
              }}
            />
          </div>
          <div className='text-sm'>
            <div className='font-semibold'>
              {parent?.firstName} {parent?.lastName}
            </div>
            <p className='mb-1'>{job.booking?.address?.city}</p>
            <p className='mb-2'>
              {typeId === 1 && (
                <span>
                  {job.booking?.numberOfChildren === 1
                    ? '1 child'
                    : `${job.booking?.numberOfChildren} children`}
                  {job.booking?.kidsAges ? `: ${job.booking.kidsAges}` : ''}
                </span>
              )}
              {typeId === 2 && (
                <span>
                  {job.booking?.numberOfChildren === 1
                    ? '1 pet'
                    : `${job.booking?.numberOfChildren} pets`}
                </span>
              )}
              {typeId === 3 && (
                <span>
                  {job.booking?.numberOfChildren === 1
                    ? '1 senior receiving care'
                    : `${job.booking?.numberOfChildren} seniors receiving care`}
                </span>
              )}
            </p>
            <p className='mb-1'>
              <span className='font-semibold'>
                {job.booking?.rate
                  ? `$${job.booking.rate} per hour`
                  : 'Rate dependent on experience'}
              </span>{' '}
              <span>({job.booking?.paymentType})</span>
            </p>
            <Button
              buttonType={ButtonType.tertiary}
              className='italic text-xs underline'
              onClick={(): void => setStep(4)}
            >
              Change
            </Button>
          </div>
        </div>
      )}

      {/* Job includes / sweeteners recap: visible on step 6 for childCare */}
      {step > 5 && typeId === 1 && (
        <div className='mb-4 pb-4 border-b border-dashed border-neutral-300'>
          {job.booking?.description && (
            <p className='mb-3 whitespace-pre-wrap'>{job.booking.description}</p>
          )}
          <div className='mb-2'>
            <div className='font-semibold'>Job includes:</div>
            {(job.booking?.includes ?? []).length > 0 ? (
              <ul className='list-disc pl-5'>
                {(job.booking?.includes ?? []).map((i) => {
                  const match = includes.find((x) => x.id === i.id);
                  return <li key={i.id}>{match?.label ?? i.label}</li>;
                })}
              </ul>
            ) : (
              <i className='text-neutral-500'>no includes selected</i>
            )}
          </div>
          <div>
            <div className='font-semibold'>Deal sweeteners:</div>
            {(job.booking?.sweeteners ?? []).length > 0 ? (
              <ul className='list-disc pl-5'>
                {(job.booking?.sweeteners ?? []).map((s) => {
                  const match = sweeteners.find((x) => x.id === s.id);
                  return <li key={s.id}>{match?.label ?? s.label}</li>;
                })}
              </ul>
            ) : (
              <i className='text-neutral-500'>no sweeteners selected</i>
            )}
          </div>
          <Button
            buttonType={ButtonType.tertiary}
            className='italic text-xs underline mt-1'
            onClick={(): void => setStep(5)}
          >
            Change
          </Button>
        </div>
      )}

      {/* Step 1: vertical */}
      {step === 1 && (
        <div className='text-center'>
          <h1 className='text-3xl font-semibold mb-6'>What care does your family need?</h1>
          <div className='space-y-3 max-w-md mx-auto'>
            {VERTICAL_CARDS.map((v) => (
              <Button
                key={v.code}
                buttonType={ButtonType.tertiary}
                onClick={(): void => setVerticalType(v.code)}
                className='bg-white border-2 border-neutral-200 rounded-xl w-full px-4 py-5 text-center block shadow-sm hover:border-sapphire hover:shadow-md transition-all'
              >
                <h3 className='font-semibold mb-1 text-neutral-900'>{v.title}</h3>
                <div className='text-sm text-neutral-600'>{v.body}</div>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: job type (skipped for householdServices) */}
      {step === 2 && (
        <div className='text-center'>
          <h1 className='text-3xl font-semibold mb-6'>When do you need this care?</h1>
          <div className='space-y-3 max-w-md mx-auto'>
            {JOB_TYPE_CARDS.map((jt) => (
              <Button
                key={jt.code}
                buttonType={ButtonType.tertiary}
                onClick={(): void => setJobType(jt.code)}
                className='bg-white border-2 border-neutral-200 rounded-xl w-full px-4 py-5 text-center block shadow-sm hover:border-sapphire hover:shadow-md transition-all'
              >
                <h3 className='font-semibold mb-1 text-neutral-900'>{jt.title}</h3>
                <div className='text-sm text-neutral-600'>{jt.body}</div>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: dates */}
      {step === 3 && (
        <div>
          <h1 className='text-3xl font-semibold mb-5 text-center'>
            {job.type === 'one_time' ? 'What date?' : 'Describe your schedule'}
          </h1>
          {job.type === 'one_time' ? (
            ((): JSX.Element => {
              const { date: startDate, time: startTime } = splitDateTime(job.booking?.start);
              const { date: endDate, time: endTime } = splitDateTime(job.booking?.end);
              const startMoment = parseLocal(job.booking?.start);
              const endMoment = parseLocal(job.booking?.end);
              const endBeforeStart =
                !!startMoment && !!endMoment && endMoment.getTime() <= startMoment.getTime();

              const onStartDateChange = (newDate: string): void => {
                const newStart = joinDateTime(newDate, startTime || '18:00');
                if (!isOvernight) {
                  const newEnd = joinDateTime(newDate, endTime || '22:00');
                  setBooking({ start: newStart, end: newEnd });
                } else {
                  setBooking({ start: newStart });
                }
              };

              const onStartTimeChange = (newTime: string): void => {
                const newStart = joinDateTime(startDate, newTime);
                const newStartMoment = parseLocal(newStart);
                // Re-anchor end to keep it on/after start when same day
                if (!isOvernight && newStartMoment && endMoment) {
                  const candidate = joinDateTime(startDate, endTime);
                  const candidateMoment = parseLocal(candidate);
                  if (
                    !candidateMoment ||
                    candidateMoment.getTime() <= newStartMoment.getTime()
                  ) {
                    // Bump end 4 hours after start (capped to end-of-day)
                    const bumped = new Date(newStartMoment);
                    bumped.setHours(Math.min(23, bumped.getHours() + 4));
                    if (bumped.getHours() === 23) bumped.setMinutes(30);
                    setBooking({ start: newStart, end: toLocalDateTime(bumped) });
                    return;
                  }
                }
                setBooking({ start: newStart });
              };

              const onEndDateChange = (newDate: string): void => {
                const newEnd = joinDateTime(newDate, endTime || '02:00');
                setBooking({ end: newEnd });
              };

              const onEndTimeChange = (newTime: string): void => {
                const baseDate = isOvernight && endDate ? endDate : startDate;
                const newEnd = joinDateTime(baseDate, newTime);
                const newEndMoment = parseLocal(newEnd);
                // Implied overnight: end-time before start-time on the same day
                if (
                  !isOvernight &&
                  newEndMoment &&
                  startMoment &&
                  newEndMoment.getTime() <= startMoment.getTime()
                ) {
                  const bumped = new Date(newEndMoment);
                  bumped.setDate(bumped.getDate() + 1);
                  setBooking({ end: toLocalDateTime(bumped) });
                  setIsOvernight(true);
                  return;
                }
                setBooking({ end: newEnd });
              };

              const toggleOvernight = (): void => {
                if (!isOvernight) {
                  // Same-day → overnight: bump end date forward by 1 day
                  if (startDate && endTime) {
                    const start = parseLocal(job.booking?.start);
                    if (start) {
                      const end = new Date(start);
                      end.setDate(end.getDate() + 1);
                      const [hStr, mStr] = endTime.split(':');
                      end.setHours(Number(hStr), Number(mStr), 0, 0);
                      setBooking({ end: toLocalDateTime(end) });
                    }
                  }
                  setIsOvernight(true);
                } else {
                  // Overnight → same-day: pin end date to start date and bump time if needed
                  if (startDate && endTime) {
                    let newEnd = joinDateTime(startDate, endTime);
                    const newEndMoment = parseLocal(newEnd);
                    if (
                      startMoment &&
                      newEndMoment &&
                      newEndMoment.getTime() <= startMoment.getTime()
                    ) {
                      const bumped = new Date(startMoment);
                      bumped.setHours(Math.min(23, bumped.getHours() + 4));
                      if (bumped.getHours() === 23) bumped.setMinutes(30);
                      newEnd = toLocalDateTime(bumped);
                    }
                    setBooking({ end: newEnd });
                  }
                  setIsOvernight(false);
                }
              };

              return (
                <div className='max-w-2xl mx-auto'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <input
                      type='date'
                      aria-label='Start date'
                      className='form-control border border-neutral-300 rounded px-3 py-2 w-44'
                      value={startDate}
                      onChange={(e): void => onStartDateChange(e.target.value)}
                    />
                    <select
                      aria-label='Start time'
                      className='form-control border border-neutral-300 rounded px-3 py-2 w-32'
                      value={startTime}
                      onChange={(e): void => onStartTimeChange(e.target.value)}
                    >
                      <option value=''>{t('pages.jobPost.schedule.startingOption')}</option>
                      {TIME_SLOTS.map((slot) => (
                        <option key={slot.value} value={slot.value}>
                          {slot.label}
                        </option>
                      ))}
                    </select>
                    <span className='font-semibold px-1'>To</span>
                    {isOvernight && (
                      <input
                        type='date'
                        aria-label='End date'
                        min={startDate || undefined}
                        className='form-control border border-neutral-300 rounded px-3 py-2 w-44'
                        value={endDate}
                        onChange={(e): void => onEndDateChange(e.target.value)}
                      />
                    )}
                    <select
                      aria-label='End time'
                      className='form-control border border-neutral-300 rounded px-3 py-2 w-32'
                      value={endTime}
                      onChange={(e): void => onEndTimeChange(e.target.value)}
                    >
                      <option value=''>{t('pages.jobPost.schedule.endingOption')}</option>
                      {TIME_SLOTS.map((slot) => (
                        <option key={slot.value} value={slot.value}>
                          {slot.label}
                        </option>
                      ))}
                    </select>
                    <Button
                      buttonType={ButtonType.tertiary}
                      className='!underline text-sm'
                      onClick={toggleOvernight}
                    >
                      {isOvernight ? 'Same day booking' : 'Overnight booking'}
                    </Button>
                  </div>
                  {endBeforeStart && (
                    <p className='text-red-600 text-sm mt-2'>
                      Booking end time must be after the start time
                    </p>
                  )}
                </div>
              );
            })()
          ) : (
            <div className='space-y-3 max-w-lg mx-auto'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <div>
                  <label className='block font-semibold mb-1'>Starting</label>
                  <input
                    type='date'
                    className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                    value={job.recurring?.startDate ?? ''}
                    onChange={(e): void =>
                      setJob((p) => ({
                        ...p,
                        recurring: { ...(p.recurring ?? {}), startDate: e.target.value },
                      }))
                    }
                  />
                </div>
                <div>
                  <label className='block font-semibold mb-1'>Ending (optional)</label>
                  <input
                    type='date'
                    className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                    value={job.recurring?.endDate ?? ''}
                    onChange={(e): void =>
                      setJob((p) => ({
                        ...p,
                        recurring: { ...(p.recurring ?? {}), endDate: e.target.value },
                      }))
                    }
                  />
                </div>
              </div>
              <div>
                <label className='block font-semibold mb-1'>
                  Guaranteed minimum hours per week
                </label>
                <input
                  type='number'
                  min={0}
                  placeholder='Optional'
                  className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                  value={job.minimumHours ?? ''}
                  onChange={(e): void =>
                    setJob((p) => ({ ...p, minimumHours: Number(e.target.value) || undefined }))
                  }
                />
              </div>
              <div>
                <label className='block font-semibold mb-2'>
                  On what days will the job occur?
                </label>
                <div className='flex flex-wrap gap-3 mb-2'>
                  {DAYS.map((d, i) => {
                    const active = (job.recurring?.days ?? []).includes(i);
                    const isNight = job.recurring?.timeOfDay?.code === 'night';
                    return (
                      <Button
                        key={d}
                        buttonType={ButtonType.tertiary}
                        aria-pressed={active}
                        aria-label={`Toggle ${d}`}
                        onClick={(): void => toggleDay(i)}
                        className='!p-0 !min-w-0 flex flex-col items-center'
                      >
                        <span className='text-xs text-neutral-600 font-light uppercase'>{d}</span>
                        <span
                          aria-hidden='true'
                          className={`mt-1 w-9 h-9 rounded-full flex items-center justify-center ${
                            !active
                              ? 'bg-neutral-200'
                              : isNight ? 'bg-sapphire' : 'bg-yellow-400'
                          }`}
                        >
                          {active && (
                            <FontAwesomeIcon
                              icon={isNight ? faMoon : faSun}
                              className='text-white text-lg'
                            />
                          )}
                        </span>
                      </Button>
                    );
                  })}
                </div>
                <label className='flex items-center gap-2 text-sm'>
                  <input
                    type='checkbox'
                    checked={!!job.recurring?.flexible}
                    onChange={(e): void =>
                      setJob((p) => ({
                        ...p,
                        recurring: { ...(p.recurring ?? {}), flexible: e.target.checked },
                      }))
                    }
                  />
                  These days are flexible.
                </label>
              </div>
              <div>
                <label className='block font-semibold mb-1'>At what times?</label>
                <select
                  className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                  value={job.recurring?.timeOfDay?.code ?? ''}
                  onChange={(e): void => {
                    const match = TIMES_OF_DAY.find((time) => time.code === e.target.value);
                    setJob((p) => ({
                      ...p,
                      recurring: { ...(p.recurring ?? {}), timeOfDay: match },
                    }));
                  }}
                >
                  <option value=''>Select...</option>
                  {TIMES_OF_DAY.map((time) => (
                    <option key={time.code} value={time.code}>
                      {time.label}
                    </option>
                  ))}
                </select>
                {job.recurring?.timeOfDay?.code === 'specific' && (
                  <div className='mt-4 space-y-3'>
                    {(job.recurring?.days ?? [])
                      .slice()
                      .sort((a, b) => a - b)
                      .map((dayIndex) => {
                        const schedule = job.recurring?.daySchedule?.[dayIndex] ?? {};
                        const startTime = schedule.startTime ?? '';
                        const endTime = schedule.endTime ?? '';
                        const hasInvalidRange =
                          startTime && endTime && startTime >= endTime;
                        const setDayTime = (field: 'startTime' | 'endTime', value: string): void => {
                          setJob((p) => {
                            const current = p.recurring?.daySchedule?.[dayIndex] ?? {};
                            const next = { ...current, [field]: value };
                            const selectedDays = p.recurring?.days ?? [];
                            const existingSchedule = p.recurring?.daySchedule ?? {};
                            const baseSchedule = { ...existingSchedule, [dayIndex]: next };
                            const isComplete = (dayTimes?: { startTime?: string; endTime?: string }): boolean =>
                              !!dayTimes?.startTime && !!dayTimes?.endTime && dayTimes.startTime < dayTimes.endTime;
                            const completeDayCount = selectedDays.filter((day) =>
                              isComplete(day === dayIndex ? next : existingSchedule[day]),
                            ).length;
                            const propagated: Record<number, { startTime?: string; endTime?: string }> =
                              isComplete(next) && completeDayCount === 1
                                ? selectedDays.reduce((scheduleByDay, day) => {
                                    scheduleByDay[day] = { ...next };
                                    return scheduleByDay;
                                  }, {} as Record<number, { startTime?: string; endTime?: string }>)
                                : baseSchedule;
                            return {
                              ...p,
                              recurring: { ...(p.recurring ?? {}), daySchedule: propagated },
                            };
                          });
                        };
                        const dayName = t(`pages.jobPost.weekdaysLong.${WEEKDAYS_LONG_KEYS[dayIndex]}`);
                        return (
                          <div key={dayIndex}>
                            <h4 className='font-semibold mb-1'>
                              {dayName}
                            </h4>
                            <div className='flex flex-wrap gap-2 items-center'>
                              <select
                                aria-label={t('pages.jobPost.schedule.dayStartTimeAriaLabel', { day: dayName })}
                                className='form-control border border-neutral-300 rounded px-3 py-2 w-32'
                                value={startTime}
                                onChange={(e): void => setDayTime('startTime', e.target.value)}
                              >
                                <option value=''>{t('pages.jobPost.schedule.startingOption')}</option>
                                {TIME_SLOTS.map((slot) => (
                                  <option key={slot.value} value={slot.value}>
                                    {slot.label}
                                  </option>
                                ))}
                              </select>
                              <span className='font-semibold px-1'>{t('pages.jobPost.schedule.to')}</span>
                              <select
                                aria-label={t('pages.jobPost.schedule.dayEndTimeAriaLabel', { day: dayName })}
                                className='form-control border border-neutral-300 rounded px-3 py-2 w-32'
                                value={endTime}
                                onChange={(e): void => setDayTime('endTime', e.target.value)}
                              >
                                <option value=''>{t('pages.jobPost.schedule.endingOption')}</option>
                                {TIME_SLOTS.map((slot) => (
                                  <option key={slot.value} value={slot.value}>
                                    {slot.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {hasInvalidRange && (
                              <p className='text-red-600 text-sm mt-1'>
                                {t('pages.jobPost.schedule.endBeforeStartDay')}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    {(job.recurring?.days ?? []).length === 0 && (
                      <p className='text-sm text-neutral-600'>
                        {t('pages.jobPost.schedule.selectAtLeastOneDay')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {errors.length > 0 && (
            <div className='mt-4 max-w-xl mx-auto text-red-600'>
              <div className='flex items-start gap-2'>
                <FontAwesomeIcon icon={faCircleExclamation} className='mt-1' />
                <div>
                  <div className='font-semibold'>Missing information:</div>
              <ul className='list-disc pl-5'>
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
                </div>
              </div>
            </div>
          )}
          <div className='mt-5 text-center'>
            <Button buttonType={ButtonType.primary} onClick={(): void => goToStep(4)}>
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: details (children count, address, rate, payment) */}
      {step === 4 && (
        <div>
          {suggestedRate?.rate && (
            <div className='flex items-start gap-3 rounded-lg bg-amber-50 border-l-4 border-amber-400 shadow-sm p-4 mb-5'>
              <div className='flex-shrink-0 w-9 h-9 rounded-full bg-amber-400 text-white flex items-center justify-center'>
                <FontAwesomeIcon icon={faStar} />
              </div>
              <div>
                <div className='font-bold text-sm tracking-wide text-amber-800 uppercase'>Pro tip</div>
                <div className='text-sm text-neutral-800 mt-0.5'>
                  Families posting at or above the average of{' '}
                  <span className='font-semibold'>${suggestedRate.rate}/hr</span> receive
                  more caregiver responses.
                </div>
              </div>
            </div>
          )}
          <h1 className='text-2xl font-semibold mb-4 text-center'>Introduce yourself and set a rate</h1>
          <ProfilePhotoUploader
            parentId={user?.id}
            pictureUrl={parent?.pictureUrl}
            onUploaded={(): void => {
              setPhotoJustUploaded(true);
              void stores.parent.refreshParent?.();
            }}
          />
          <div className='space-y-3 max-w-xl mx-auto'>
            {showKidsSelect && (
              <div>
                <label className={`block font-semibold mb-1 ${errorFields.has('numberOfChildren') ? 'text-red-600' : ''}`}>Receiving Care</label>
                <select
                  className={`form-control w-full border rounded px-3 py-2 ${errorFields.has('numberOfChildren') ? 'border-red-500' : 'border-neutral-300'}`}
                  value={job.booking?.numberOfChildren ?? 1}
                  onChange={(e): void =>
                    setBooking({ numberOfChildren: Number(e.target.value) })
                  }
                >
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>
                      {n}{' '}
                      {typeId === 1
                        ? n === 1 ? 'child' : 'children'
                        : typeId === 2
                          ? n === 1 ? 'pet' : 'pets'
                          : n === 1 ? 'senior' : 'seniors'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {typeId === 1 && (
              <div>
                <label className={`block font-semibold mb-1 ${errorFields.has('kidsAges') ? 'text-red-600' : ''}`}>Children&apos;s ages</label>
                <input
                  type='text'
                  className={`form-control w-full border rounded px-3 py-2 ${errorFields.has('kidsAges') ? 'border-red-500' : 'border-neutral-300'}`}
                  placeholder='e.g. 3 & 5'
                  value={job.booking?.kidsAges ?? ''}
                  onChange={(e): void => setBooking({ kidsAges: e.target.value })}
                />
              </div>
            )}

            <h2 className='text-lg font-semibold text-center mt-5 mb-0'>Job Location</h2>
            <p className='text-sm text-center text-neutral-600 mb-2'>
              Street is NOT shown to sitters - only distance
            </p>
            <input
              type='text'
              placeholder='Street'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              value={job.booking?.address?.street ?? ''}
              onChange={(e): void => setAddress({ street: e.target.value })}
            />
            <input
              type='text'
              placeholder='Apt, suite, unit, etc.'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              value={job.booking?.address?.street2 ?? ''}
              onChange={(e): void => setAddress({ street2: e.target.value })}
            />
            <div className='grid grid-cols-3 gap-2'>
              <input
                type='text'
                placeholder='City'
                aria-label={t('pages.jobPost.fields.cityAriaLabel')}
                className={`col-span-2 form-control border rounded px-3 py-2 ${errorFields.has('city') ? 'border-red-500' : 'border-neutral-300'}`}
                value={job.booking?.address?.city ?? ''}
                onChange={(e): void => setAddress({ city: e.target.value })}
              />
              <input
                type='text'
                placeholder='State'
                aria-label={t('pages.jobPost.fields.stateAriaLabel')}
                maxLength={2}
                className={`form-control border rounded px-3 py-2 ${errorFields.has('state') ? 'border-red-500' : 'border-neutral-300'}`}
                value={job.booking?.address?.state ?? ''}
                onChange={(e): void =>
                  setAddress({ state: e.target.value.toUpperCase() })
                }
              />
            </div>
            <input
              type='tel'
              maxLength={5}
              placeholder='ZIP code'
              aria-label={t('pages.jobPost.fields.zipCodeAriaLabel')}
              className={`form-control w-full border rounded px-3 py-2 ${errorFields.has('zip') ? 'border-red-500' : 'border-neutral-300'}`}
              value={job.booking?.address?.zip ?? ''}
              onChange={(e): void => setAddress({ zip: e.target.value })}
            />
            <input
              type='text'
              placeholder='Neighborhood (optional)'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              value={job.booking?.address?.neighborhood ?? ''}
              onChange={(e): void => setAddress({ neighborhood: e.target.value })}
            />

            <h2 className='text-lg font-semibold text-center mt-5 mb-0'>Payment Information</h2>
            {suggestedRate?.label && suggestedRate.rate && (
              <h5 className='text-center text-sm text-neutral-600'>
                {suggestedRate.label}: ${suggestedRate.rate}/hr
              </h5>
            )}

            <div className='flex items-center justify-center gap-2 py-2'>
              <label htmlFor='negotiable-rate-toggle' className='text-sm'>
                Rate dependent on experience
              </label>
              <ToggleSwitch
                id='negotiable-rate-toggle'
                checked={negotiableRate}
                handleClick={(): void => {
                  setNegotiableRate((prev) => {
                    const next = !prev;
                    if (next) setBooking({ rate: undefined });
                    return next;
                  });
                }}
              />
              <Button
                id='negotiable-rate-info'
                className='relative group text-neutral-500 cursor-help'
                aria-label={t('pages.jobPost.fields.rateInfoAriaLabel')}
                aria-describedby='negotiable-rate-tooltip'
              >
                <FontAwesomeIcon icon={faCircleInfo} />
                <span
                  id='negotiable-rate-tooltip'
                  role='tooltip'
                  className='pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block group-focus:block w-64 bg-neutral-800 text-white text-xs rounded px-3 py-2 z-20 text-center'
                >
                  {t('pages.jobPost.fields.rateInfoTooltip')}
                </span>
              </Button>
            </div>

            <div className='flex flex-wrap items-center gap-6'>
              <label
                htmlFor={negotiableRate ? 'paymentType' : 'hourlyRate'}
                className={`font-semibold min-w-[7rem] ${errorFields.has('rate') || errorFields.has('paymentType') ? 'text-red-600' : ''}`}
              >
                {negotiableRate
                  ? t('pages.jobPost.fields.paymentMethodLabel')
                  : t('pages.jobPost.fields.ratePerHourLabel')}
              </label>
              {!negotiableRate && (
                <div
                  className={`flex items-center border rounded w-32 ${errorFields.has('rate') ? 'border-red-500' : 'border-neutral-300'}`}
                >
                  <span className='px-2 bg-neutral-100'>$</span>
                  <input
                    id='hourlyRate'
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    aria-label='Hourly rate'
                    className='flex-1 px-2 py-2 w-full min-w-0'
                    value={job.booking?.rate ?? ''}
                    onChange={(e): void =>
                      setBooking({ rate: Number(e.target.value) || undefined })
                    }
                  />
                </div>
              )}
              <select
                id='paymentType'
                aria-label={t('pages.jobPost.fields.paymentMethodAriaLabel')}
                className={`flex-1 min-w-[10rem] form-control border rounded px-3 py-2 font-bold ${errorFields.has('paymentType') ? 'border-red-500' : 'border-neutral-300'}`}
                value={job.booking?.paymentType ?? ''}
                onChange={(e): void => setBooking({ paymentType: e.target.value })}
              >
                {PAYMENT_TYPES.map((p) => (
                  <option key={p.code} value={p.code} disabled={!p.code}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {errors.length > 0 && (
            <div className='mt-4 max-w-xl mx-auto text-red-600'>
              <div className='flex items-start gap-2'>
                <FontAwesomeIcon icon={faCircleExclamation} className='mt-1' />
                <div>
                  <div className='font-semibold'>Missing information:</div>
                  <ul className='list-disc pl-5'>
                    {errors.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          <div className='mt-4 text-center'>
            <Button
              buttonType={ButtonType.primary}
              onClick={(): void => goToStep(5)}
              disabled={!hasPhoto}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Step 5: description + includes + sweeteners */}
      {step === 5 && (
        <div>
          <h1 className='text-2xl font-semibold mb-4 text-center'>Describe the job</h1>
          {typeId === 1 && (
            <>
              <h3 className='font-semibold mb-2'>Job includes</h3>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5'>
                {includes.map((inc) => (
                  <label key={inc.id} className='flex items-center gap-2 text-sm'>
                    <input
                      type='checkbox'
                      checked={!!inc.selected}
                      onChange={(): void =>
                        setIncludes((prev) =>
                          prev.map((i) =>
                            i.id === inc.id ? { ...i, selected: !i.selected } : i,
                          ),
                        )
                      }
                    />
                    {inc.label}
                  </label>
                ))}
              </div>
              <h3 className='font-semibold mb-2'>Sweeten the deal</h3>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5'>
                {sweeteners.map((s) => (
                  <label key={s.id} className='flex items-center gap-2 text-sm'>
                    <input
                      type='checkbox'
                      checked={!!s.selected}
                      onChange={(): void =>
                        setSweeteners((prev) =>
                          prev.map((i) =>
                            i.id === s.id ? { ...i, selected: !i.selected } : i,
                          ),
                        )
                      }
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </>
          )}
          <label className='block font-semibold mb-1'>Details</label>
          <div className='text-right text-xs text-neutral-500 mb-1'>
            {remaining} characters remaining
          </div>
          <textarea
            rows={6}
            maxLength={MAX_DESCRIPTION}
            className='form-control w-full border border-neutral-300 rounded p-2 mb-2'
            placeholder='Describe the job, the family, and any specific needs.'
            value={description}
            onChange={(e): void => setBooking({ description: e.target.value })}
          />
          <p className='text-sm font-semibold text-neutral-700 mb-4'>
            For your own privacy and security, please do not include contact information above.
          </p>
          {errors.length > 0 && (
            <div className='mb-4 text-red-600'>
              <div className='flex items-start gap-2'>
                <FontAwesomeIcon icon={faCircleExclamation} className='mt-1' />
                <div>
                  <div className='font-semibold'>Missing information:</div>
                  <ul className='list-disc pl-5'>
                    {errors.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          <div className='text-center'>
            <Button buttonType={ButtonType.primary} onClick={(): void => goToStep(6)}>
              Continue
            </Button>
            {(isFromNux || presetType) && (
              <Button
                id='job-post-skip-to-search'
                buttonType={ButtonType.secondary}
                className='ml-3'
                onClick={skipToSearch}
              >
                Skip to Search
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Step 6: screening questions */}
      {step === 6 && (
        <div>
          <h1 className='text-2xl font-semibold mb-4 text-center'>
            Add screening questions (optional)
          </h1>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div className='space-y-3'>
              {questions.map((q, i) => {
                const left = MAX_DESCRIPTION - (q.text?.length ?? 0);
                return (
                  <div key={i}>
                    <div className='text-right text-xs text-neutral-500 mb-1'>
                      {left} characters remaining
                    </div>
                    <textarea
                      rows={4}
                      maxLength={MAX_DESCRIPTION}
                      aria-label={`Screening question ${i + 1}`}
                      className='form-control w-full border border-neutral-300 rounded p-2'
                      value={q.text ?? ''}
                      onChange={(e): void => updateQuestion(i, e.target.value)}
                    />
                  </div>
                );
              })}
              {questions.length < MAX_QUESTIONS &&
                (questions[questions.length - 1]?.text?.length ?? 0) > 3 && (
                  <div className='text-center'>
                    <Button
                      id='add-screening-question'
                      buttonType={ButtonType.tertiary}
                      className='underline text-sapphire'
                      onClick={addScreeningQuestion}
                    >
                      Add another screening question
                    </Button>
                  </div>
                )}
            </div>

            {typeId === 1 && sampleQuestions.length > 0 && (
              <div className='text-center'>
                <p className='font-semibold mb-2'>SAMPLE QUESTIONS</p>
                <div className='relative border border-neutral-200 rounded p-4 min-h-[120px] flex items-center justify-center'>
                  <Button
                    buttonType={ButtonType.tertiary}
                    aria-label='Previous sample question'
                    className='absolute left-2 text-sapphire px-2 py-1'
                    onClick={(): void =>
                      setShownSampleIndex(
                        (i) => (i - 1 + sampleQuestions.length) % sampleQuestions.length,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </Button>
                  <div className='px-8'>
                    {sampleQuestions[shownSampleIndex]?.question}
                  </div>
                  <Button
                    buttonType={ButtonType.tertiary}
                    aria-label='Next sample question'
                    className='absolute right-2 text-sapphire px-2 py-1'
                    onClick={(): void =>
                      setShownSampleIndex((i) => (i + 1) % sampleQuestions.length)
                    }
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Button>
                </div>
                <div className='mt-3'>
                  <Button
                    id='add-sample-question'
                    buttonType={ButtonType.secondary}
                    disabled={
                      questions.length >= MAX_QUESTIONS &&
                      !!questions[MAX_QUESTIONS - 1]?.text
                    }
                    onClick={(): void => {
                      const q = sampleQuestions[shownSampleIndex]?.question;
                      if (!q) return;
                      setJob((prev) => {
                        const qs = [...(prev.questions ?? [])];
                        const lastIdx = qs.length - 1;
                        if (lastIdx >= 0 && !qs[lastIdx].text) {
                          qs[lastIdx] = { ...qs[lastIdx], text: q };
                        } else if (qs.length < MAX_QUESTIONS) {
                          qs.push({ text: q });
                        }
                        return { ...prev, questions: qs };
                      });
                      setSampleQuestions((prev) =>
                        prev.filter((_, idx) => idx !== shownSampleIndex),
                      );
                      setShownSampleIndex(0);
                    }}
                  >
                    Add this question
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Post / Update job CTA (shown on final step) */}
      {step === maxStep && step > 2 && (
        <div className='max-w-sm mx-auto mt-6 text-center'>
          <Button
            id='save-job'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={saveJob}
          >
            {submitting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className='mr-2' />
                Posting…
              </>
            ) : editingJobId ? 'Update Job!' : 'Post Job!'}
          </Button>
          <p className='text-sm text-neutral-600 mt-2'>
            Then select from interested caregivers.
          </p>
        </div>
      )}

      {/* Progress indicator — matches Angular: circles for steps 2..maxStep, where
          a circle is "done" once you've advanced past it, so step 3 shows the
          first circle already done and the second active. */}
      {step > 2 && (
        <div className='flex justify-center gap-2 mt-6'>
          {progressSteps.slice(1).map((s) => (
            <span
              key={s}
              className={`w-3 h-3 rounded-full inline-block ${
                step === s
                  ? 'bg-sapphire'
                  : step > s
                    ? 'bg-emerald-500'
                    : 'border border-neutral-300'
              }`}
            />
          ))}
        </div>
      )}

      {/* Cancel link */}
      {step > 2 && (
        <div className='text-right mt-4'>
          <Button
            id='cancel-job-creation-edit'
            buttonType={ButtonType.tertiary}
            className='text-sm font-semibold underline'
            onClick={(): void => navigate('/posts')}
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Reassurance cards shown on steps 1-2 */}
      {step < 3 && (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mt-10 text-center'>
          <div>
            <FontAwesomeIcon icon={faShieldCheck} className='text-4xl text-sapphire mb-3' />
            <h4 className='font-semibold'>YOU&apos;RE IN CONTROL</h4>
            <p className='text-sm text-neutral-600'>
              There is no obligation to hire or respond to any caregiver who applies to your job.
            </p>
          </div>
          <div>
            <FontAwesomeIcon icon={faStar} className='text-4xl text-amber-400 mb-3' />
            <h4 className='font-semibold'>REVIEWS AND REPEAT FAMILIES</h4>
            <p className='text-sm text-neutral-600'>
              Look for caregivers who have repeat families or have great reviews.
            </p>
          </div>
          <div>
            <FontAwesomeIcon icon={faMessageQuestion} className='text-4xl text-sapphire mb-3' />
            <h4 className='font-semibold'>SCREENING QUESTIONS</h4>
            <p className='text-sm text-neutral-600'>
              Learn more about the interested caregivers to find the best one for your job.
            </p>
          </div>
        </div>
      )}
    </div>
  );
});
